import type {
  AuthMFAEnrollTOTPResponse,
  Factor,
} from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

/**
 * Thin, typed wrappers around `supabase.auth.mfa.*` for the AAL2 TOTP rollout
 * (spec-14b Phase 2). Frontend-only: every call goes browser → Supabase Auth;
 * the platform backend is never touched. Founders (magic-link) never reach this
 * code — all MFA UI is gated to staff roles (admin/team/fundraiser) at the
 * component level.
 *
 * A session reaches `aal2` only once a TOTP factor is enrolled AND verified, so
 * enrolment must finish with a successful `verify` and step-up re-runs the same
 * challenge/verify pair against the already-verified factor.
 */

/** The TOTP enrolment payload the UI renders (QR data-URI + text secret). */
export interface TotpEnrolment {
  factorId: string
  /** SVG QR code, already a `data:image/svg+xml;...` URI. */
  qrCode: string
  /** The raw TOTP secret, shown as a copy-able fallback to the QR code. */
  secret: string
}

/**
 * Whether the session can/should step up to aal2, derived from
 * `getAuthenticatorAssuranceLevel`. `needsStepUp` is true only when a verified
 * factor exists (`nextLevel === 'aal2'`) but the session is still `aal1`.
 */
export interface AssuranceState {
  currentLevel: string | null
  nextLevel: string | null
  needsStepUp: boolean
}

/** Read the session's current/next AAL and whether a step-up is pending. */
export async function getAssuranceState(): Promise<AssuranceState> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  if (error || !data) {
    return { currentLevel: null, nextLevel: null, needsStepUp: false }
  }
  return {
    currentLevel: data.currentLevel,
    nextLevel: data.nextLevel,
    needsStepUp: data.currentLevel === 'aal1' && data.nextLevel === 'aal2',
  }
}

/** All TOTP factors on the account (verified and unverified). */
export async function listTotpFactors(): Promise<Factor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error || !data) return []
  return data.all.filter((factor) => factor.factor_type === 'totp')
}

/** True when the account already holds a verified TOTP factor. */
export async function hasVerifiedTotpFactor(): Promise<boolean> {
  const factors = await listTotpFactors()
  return factors.some((factor) => factor.status === 'verified')
}

/**
 * Begin TOTP enrolment. If an *unverified* factor is left over from an abandoned
 * attempt we reuse it (Supabase rejects a second un-named TOTP factor with a
 * "factor already exists" error), so re-entry is idempotent.
 */
export async function startTotpEnrolment(): Promise<TotpEnrolment> {
  const factors = await listTotpFactors()
  const stale = factors.find((factor) => factor.status === 'unverified')
  if (stale) {
    // Drop the abandoned factor so enroll() can mint a fresh QR/secret; if the
    // removal fails we fall through and let enroll() surface the real error.
    await removeFactor(stale.id).catch(() => undefined)
  }

  const { data, error } = (await supabase.auth.mfa.enroll({
    factorType: 'totp',
  })) as AuthMFAEnrollTOTPResponse
  if (error || !data) {
    throw new Error(error?.message ?? 'Inschrijven mislukt.')
  }
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  }
}

/**
 * Verify a 6-digit TOTP code against a factor. Used both to finish enrolment
 * (which stamps the factor verified → session aal2) and to step up an existing
 * session on login. Throws with a Dutch message on a bad/expired code.
 */
export async function verifyTotpCode(factorId: string, code: string): Promise<void> {
  const challenge = await supabase.auth.mfa.challenge({ factorId })
  if (challenge.error || !challenge.data) {
    throw new Error(challenge.error?.message ?? 'Verificatie mislukt.')
  }
  const verify = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.data.id,
    code,
  })
  if (verify.error) {
    throw new Error(verify.error.message)
  }
}

/** Remove a factor (e.g. an unverified one left by an abandoned enrolment). */
export async function removeFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) {
    throw new Error(error.message)
  }
}
